import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { httpsCallable } from 'firebase/functions'
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore'
import { db, functions } from '@/lib/firebase'
import { useAuth } from '@/context/AuthContext'

type GuestFormState = {
  id: string
  fullName: string
  email: string
  attending: Record<string, string>
  bus: Record<string, string>
  plusOneName: string
  bringingPlusOne: string
  mealChoice: string
  plusOneMealChoice: string
  message: string
  events: string[]
  plusOneAllowed: boolean
  isPrimary: boolean
  isChildParent: boolean
}

const SUGGESTED_ACCOMMODATIONS = [
  'Hotel Los Llanos ****',
  'Eurostars Gran Hotel ****',
  'Parador de Albacete ****',
  'Hotel Europa ***',
  'Hotel Florida ***',
  'Hotel San Antonio ****',
  'Hotel Universidad ***',
  'Apartamentos Urbanlux',
];

export default function RSVPForm() {
  const { t } = useTranslation()
  const { user } = useAuth()

  const [guests, setGuests] = useState<GuestFormState[]>([])
  const [childrenCount, setChildrenCount] = useState('0')
  const [plusChildrenAllowed, setPlusChildrenAllowed] = useState(false)
  const [childrenNames, setChildrenNames] = useState<string[]>([])
  const [childrenDietary, setChildrenDietary] = useState<string[]>([])

  const [status, setStatus] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [loadingConfig, setLoadingConfig] = useState(true)
  const [tableNumber, setTableNumber] = useState('')
  const [accommodation, setAccommodation] = useState('')
  const [accommodationCheckIn, setAccommodationCheckIn] = useState('')
  const [accommodationCheckOut, setAccommodationCheckOut] = useState('')
  const [showAccommodationSuggestions, setShowAccommodationSuggestions] = useState(false)
  const accommodationRef = React.useRef<HTMLDivElement>(null)

  useEffect(() => {
    async function fetchPartyConfig() {
      if (!user) return;
      try {
        const guestRef = doc(db, 'guests', user.uid);
        const guestSnap = await getDoc(guestRef);

        // Admin Bypass: If guest doc doesn't exists, check if user is admin
        if (!guestSnap.exists()) {
          const idTokenResult = await user.getIdTokenResult();
          if (idTokenResult.claims.role === 'admin') {
            // Return a mock guest party for admins to see/test the form
            setGuests([{
              id: user.uid,
              fullName: user.displayName || 'Admin User',
              email: user.email || '',
              events: ['IE', 'ES', 'PB'],
              plusOneAllowed: true,
              isPrimary: true,
              isChildParent: true,
              attending: { ie: 'yes', es: 'yes', pb: 'yes' },
              bus: { ie: '', esTo: '', esReturn: '' },
              plusOneName: '',
              bringingPlusOne: 'no',
              mealChoice: '',
              plusOneMealChoice: '',
              message: ''
            }]);
            setAccommodation('');
            setAccommodationCheckIn('');
            setAccommodationCheckOut('');
            setLoadingConfig(false);
          }
          return;
        }

        const primaryData = guestSnap.data();
        const partyHash = primaryData.inviteCodeHash;

        if (!partyHash) {
          throw new Error("No party hash found");
        }

        const partyQuery = query(collection(db, 'guests'), where('inviteCodeHash', '==', partyHash));
        const partyDocs = await getDocs(partyQuery);

        const loadedGuests: GuestFormState[] = [];
        let anyChildrenAllowed = false;

        partyDocs.forEach(docSnap => {
          const data = docSnap.data();
          if (data.plusChildren) anyChildrenAllowed = true;

          const guestEvents = (data.events && Array.isArray(data.events) && data.events.length > 0)
            ? data.events
            : ['IE', 'ES', 'PB'];
          const initialAttending: Record<string, string> = {};
          guestEvents.forEach((cel: string) => {
            const key = cel.toLowerCase();
            initialAttending[key] = data.status?.[key] || 'yes';
          });

          const initialBus: Record<string, string> = {
            ie: data.bus?.ie || '',
            esTo: data.bus?.esTo || '',
            esReturn: data.bus?.esReturn || ''
          };

          let val = '';
          let plusOneVal = '';
          if (data.dietary) {
            if (typeof data.dietary === 'string') {
              val = data.dietary;
            } else {
              if (data.dietary.guest) val = typeof data.dietary.guest === 'object' ? '' : String(data.dietary.guest);
              if (data.dietary.plusOne) plusOneVal = typeof data.dietary.plusOne === 'object' ? '' : String(data.dietary.plusOne);
            }
          }

          const isPrimary = docSnap.id === user.uid;

          if (isPrimary && data.children !== undefined) {
            setChildrenCount(String(data.children));
          }

          if (isPrimary && data.childrenNames) {
            setChildrenNames(data.childrenNames);
          }

          if (isPrimary && data.dietary && data.dietary.children) {
            setChildrenDietary(data.dietary.children);
          }

          if (isPrimary && data.tableNumber) {
            setTableNumber(data.tableNumber);
          }

          if (isPrimary && data.accommodation) {
            setAccommodation(data.accommodation);
          }

          if (isPrimary && data.accommodationCheckIn) {
            setAccommodationCheckIn(data.accommodationCheckIn);
          }

          if (isPrimary && data.accommodationCheckOut) {
            setAccommodationCheckOut(data.accommodationCheckOut);
          }

          loadedGuests.push({
            id: docSnap.id,
            fullName: data.fullName || data.name || data.firstName || '',
            email: data.email || '',
            events: guestEvents,
            plusOneAllowed: !!data.plusOneAllowed,
            isPrimary,
            isChildParent: !!data.plusChildren,
            attending: initialAttending,
            bus: initialBus,
            plusOneName: data.plusOneName || '',
            bringingPlusOne: data.plusOneName ? 'yes' : 'no',
            mealChoice: val,
            plusOneMealChoice: plusOneVal,
            message: data.notes || ''
          });
        });

        loadedGuests.sort((a, b) => a.isPrimary ? -1 : (b.isPrimary ? 1 : 0));

        setGuests(loadedGuests);
        setPlusChildrenAllowed(anyChildrenAllowed);

      } catch (error) {
        // Silently handled
      } finally {
        setLoadingConfig(false);
      }
    }

    fetchPartyConfig();
  }, [user]);

  // Close accommodation dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (accommodationRef.current && !accommodationRef.current.contains(e.target as Node)) {
        setShowAccommodationSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const count = parseInt(childrenCount, 10) || 0;
    setChildrenNames(prev => {
      if (prev.length < count) {
        return [...prev, ...Array(count - prev.length).fill('')];
      }
      return prev.slice(0, count);
    });
    setChildrenDietary(prev => {
      if (prev.length < count) {
        return [...prev, ...Array(count - prev.length).fill('')];
      }
      return prev.slice(0, count);
    });
  }, [childrenCount]);

  function updateGuest(id: string, field: keyof GuestFormState, value: any) {
    setGuests(prev => prev.map(g => g.id === id ? { ...g, [field]: value } : g));
  }

  function updateGuestAttending(id: string, eventInfo: string, value: string) {
    setGuests(prev => prev.map(g => {
      if (g.id === id) {
        return { ...g, attending: { ...g.attending, [eventInfo]: value } };
      }
      return g;
    }));
  }

  function updateGuestBus(id: string, busField: string, value: string) {
    setGuests(prev => prev.map(g => {
      if (g.id === id) {
        return { ...g, bus: { ...g.bus, [busField]: value } };
      }
      return g;
    }));
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setStatus(null)
    setSubmitting(true)

    try {
      const updateGuestRsvp = httpsCallable(functions, 'updateGuestRsvp');

      for (const guest of guests) {
        const payload: any = {
          guestId: guest.id,
          rsvp: guest.attending,
          bus: guest.bus,
          dietary: { 
            guest: guest.mealChoice, 
            plusOne: guest.plusOneMealChoice,
            children: guest.isPrimary && plusChildrenAllowed ? childrenDietary : []
          },
          notes: guest.message
        };

        if (guest.plusOneAllowed) {
          if (guest.bringingPlusOne === 'yes') {
            payload.plusOneName = guest.plusOneName;
          } else {
            payload.plusOneName = null;
            payload.dietary.plusOne = '';
          }
        }

        if (guest.isPrimary && plusChildrenAllowed) {
          payload.children = parseInt(childrenCount, 10) || 0;
          payload.childrenNames = childrenNames;
        } else if (!guest.isPrimary && plusChildrenAllowed) {
          payload.children = 0;
          payload.childrenNames = [];
        }

        await updateGuestRsvp(payload);
      }

      // Save accommodation on the primary guest
      const primaryGuest = guests.find(g => g.isPrimary);
      if (primaryGuest) {
        const anyAttendingEs = guests.some(g => g.attending.es === 'yes');
        if (anyAttendingEs) {
          await updateGuestRsvp({
            guestId: primaryGuest.id,
            accommodation: accommodation,
            accommodationCheckIn: accommodationCheckIn,
            accommodationCheckOut: accommodationCheckOut
          });
        }
      }

      setIsSuccess(true)
    } catch (error) {
      setStatus(t('rsvp.form.error_generic'))
    } finally {
      setSubmitting(false)
    }
  }

  if (loadingConfig) {
    return <div className="p-4 text-center">{t('common.loading')}</div>;
  }

  if (isSuccess) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-6 text-center space-y-6 animate-fade-in border border-[#e2d6c8] rounded-2xl bg-[#fdfaf6]/50 my-12 shadow-sm">
        <div className="w-20 h-20 rounded-full bg-[#bf9c56]/15 flex items-center justify-center mb-2">
          <svg className="w-10 h-10 text-[#bf9c56]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-3xl md:text-4xl font-serif text-[#5a3b2b] italic">
          {t('rsvp.form.success_thanks')}
        </h3>
        <div className="space-y-2">
          <p className="text-xl font-sans text-[#5a3b2b] font-medium">{t('rsvp.form.success_recorded')}</p>
          <p className="text-lg font-sans text-[#5a3b2b]/80">{t('rsvp.form.success_waiting')}</p>
        </div>
        {tableNumber && (
          <div className="mt-4 px-6 py-4 bg-[#bf9c56]/10 border border-[#bf9c56]/20 rounded-xl">
            <p className="text-sm font-sans text-[#5a3b2b]/60 uppercase tracking-widest font-bold mb-1">
              {t('rsvp.form.your_table', { defaultValue: 'Your Table' })}
            </p>
            <p className="text-2xl font-serif text-[#bf9c56] italic">{tableNumber}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <form className="space-y-16" onSubmit={handleSubmit} noValidate>
      {guests.map((guest, index) => (
        <div key={guest.id} className="space-y-10 animate-fade-in relative mb-12">
          {/* Guest Name using Morello - smaller size sitting on the line */}
          <div className="border-b border-[#e2d6c8] mb-8 pb-0">
            <h3 className="text-2xl md:text-3xl font-serif text-[#5a3b2b] italic font-light relative top-[6px]">
              {guest.fullName || `Guest ${index + 1}`}
            </h3>
          </div>

          <div className="space-y-8">
            <div className="flex flex-col space-y-2">
              <label htmlFor={`email-${guest.id}`} className="text-base font-sans tracking-wide text-[#5a3b2b] block">
                Email
              </label>
              <input
                id={`email-${guest.id}`}
                type="email"
                name={`email-${guest.id}`}
                className="w-full bg-transparent border-b-2 border-[#e2d6c8] focus:ring-2 focus:ring-[#bf9c56]/25 focus:border-[#bf9c56] focus:outline-none px-2 py-3 font-sans text-base text-[#5a3b2b] transition-all rounded-t-sm"
                placeholder="you@example.com"
                value={guest.email}
                onChange={(e) => updateGuest(guest.id, 'email', e.target.value)}
              />
            </div>

            {guest.events.map((cel) => {
              const celKey = cel.toLowerCase();
              const legendText = t(`rsvp.form.events.${celKey}_legend`, { defaultValue: `Will you be attending the ${cel} event?` });

              return (
                <div key={cel} className="space-y-4">
                  <legend className="text-base font-sans tracking-wide text-[#5a3b2b] block">{legendText}</legend>
                  <div className="flex flex-wrap gap-4 sm:gap-8">
                    {['yes', 'no'].map((value) => (
                      <label key={`${cel}-${value}`} className="flex items-center gap-3 cursor-pointer group min-h-[44px]">
                        <input
                          type="radio"
                          name={`attending-${guest.id}-${celKey}`}
                          value={value}
                          checked={guest.attending[celKey] === value}
                          className="w-5 h-5 accent-[#bf9c56] text-[#bf9c56] bg-transparent border-[#bf9c56] focus:ring-2 focus:ring-[#bf9c56]/25 focus:ring-offset-2 focus:outline-none cursor-pointer"
                          onChange={(e) => updateGuestAttending(guest.id, celKey, e.target.value)}
                        />
                        <span className="text-base font-sans text-[#5a3b2b] cursor-pointer">
                          {value === 'yes' ? t('rsvp.form.attending_yes') : t('rsvp.form.attending_no')}
                        </span>
                      </label>
                    ))}
                  </div>

                  {celKey === 'ie' && guest.attending.ie === 'yes' && (
                    <div className="space-y-4 mt-6 p-4 bg-[#fdfaf6]/40 border border-[#e2d6c8]/50 rounded-lg animate-slide-up">
                      <legend className="text-base font-sans tracking-wide text-[#5a3b2b] block">
                        {t('rsvp.form.bus_ie_legend', { defaultValue: 'Will you take the bus to the Irish celebration?' })}
                      </legend>
                      <div className="flex flex-wrap gap-4 sm:gap-8">
                        {['yes', 'no'].map((value) => (
                          <label key={`bus-ie-${value}`} className="flex items-center gap-3 cursor-pointer group min-h-[44px]">
                            <input
                              type="radio"
                              name={`busIe-${guest.id}`}
                              value={value}
                              checked={guest.bus.ie === value}
                              className="w-5 h-5 accent-[#bf9c56] text-[#bf9c56] bg-transparent border-[#bf9c56] focus:ring-2 focus:ring-[#bf9c56]/25 focus:ring-offset-2 focus:outline-none cursor-pointer"
                              onChange={(e) => updateGuestBus(guest.id, 'ie', e.target.value)}
                            />
                            <span className="text-base font-sans text-[#5a3b2b] cursor-pointer">
                              {value === 'yes' ? t('rsvp.form.yes', { defaultValue: 'Yes' }) : t('rsvp.form.no', { defaultValue: 'No' })}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {celKey === 'es' && guest.attending.es === 'yes' && (
                    <div className="space-y-6 mt-6 p-4 bg-[#fdfaf6]/40 border border-[#e2d6c8]/50 rounded-lg animate-slide-up">
                      <div className="space-y-4">
                        <legend className="text-base font-sans tracking-wide text-[#5a3b2b] block">
                          {t('rsvp.form.bus_es_to_legend', { defaultValue: 'Will you take the bus to the Spanish wedding venue?' })}
                        </legend>
                        <div className="flex flex-wrap gap-4 sm:gap-8">
                          {['yes', 'no'].map((value) => (
                            <label key={`bus-es-to-${value}`} className="flex items-center gap-3 cursor-pointer group min-h-[44px]">
                              <input
                                type="radio"
                                name={`busEsTo-${guest.id}`}
                                value={value}
                                checked={guest.bus.esTo === value}
                                className="w-5 h-5 accent-[#bf9c56] text-[#bf9c56] bg-transparent border-[#bf9c56] focus:ring-2 focus:ring-[#bf9c56]/25 focus:ring-offset-2 focus:outline-none cursor-pointer"
                                onChange={(e) => updateGuestBus(guest.id, 'esTo', e.target.value)}
                              />
                              <span className="text-base font-sans text-[#5a3b2b] cursor-pointer">
                                {value === 'yes' ? t('rsvp.form.yes', { defaultValue: 'Yes' }) : t('rsvp.form.no', { defaultValue: 'No' })}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                      
                      <div className="space-y-4">
                        <legend className="text-base font-sans tracking-wide text-[#5a3b2b] block">
                          {t('rsvp.form.bus_es_return_legend', { defaultValue: 'Which bus will you take back from the Spanish wedding?' })}
                        </legend>
                        <div className="flex flex-col gap-3">
                          {[
                            { value: 'early', label: 'Early Bus (1:30am)' },
                            { value: 'late', label: 'Late Bus (5:00am)' },
                            { value: 'none', label: 'I will not take the return bus' }
                          ].map((option) => (
                            <label key={`bus-es-return-${option.value}`} className="flex items-center gap-3 cursor-pointer group min-h-[44px]">
                              <input
                                type="radio"
                                name={`busEsReturn-${guest.id}`}
                                value={option.value}
                                checked={guest.bus.esReturn === option.value}
                                className="w-5 h-5 accent-[#bf9c56] text-[#bf9c56] bg-transparent border-[#bf9c56] focus:ring-2 focus:ring-[#bf9c56]/25 focus:ring-offset-2 focus:outline-none cursor-pointer"
                                onChange={(e) => updateGuestBus(guest.id, 'esReturn', e.target.value)}
                              />
                              <span className="text-base font-sans text-[#5a3b2b] cursor-pointer">
                                {t(`rsvp.form.bus_es_return_${option.value}`, { defaultValue: option.label })}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {guest.plusOneAllowed && (
            <div className="space-y-8 mt-8">
              <div className="space-y-4">
                <legend className="text-base font-sans tracking-wide text-[#5a3b2b] block">{t('rsvp.form.plus_one_legend')}</legend>
                <div className="flex flex-wrap gap-4 sm:gap-8">
                  {['yes', 'no'].map((value) => (
                    <label key={value} className="flex items-center gap-3 cursor-pointer group min-h-[44px]">
                      <input
                        type="radio"
                        name={`bringingPlusOne-${guest.id}`}
                        value={value}
                        checked={guest.bringingPlusOne === value}
                        className="w-5 h-5 accent-[#bf9c56] text-[#bf9c56] bg-transparent border-[#bf9c56] focus:ring-2 focus:ring-[#bf9c56]/25 focus:ring-offset-2 focus:outline-none cursor-pointer"
                        onChange={(e) => updateGuest(guest.id, 'bringingPlusOne', e.target.value)}
                      />
                      <span className="text-base font-sans text-[#5a3b2b] cursor-pointer">
                        {value === 'yes' ? t('rsvp.form.plus_one_yes') : t('rsvp.form.plus_one_no')}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {guest.bringingPlusOne === 'yes' && (
                <div className="space-y-8 animate-slide-up bg-[#fdfaf6]/40 p-6 rounded-lg border border-[#e2d6c8]/50 mt-4">
                  <div className="flex flex-col space-y-2">
                    <label htmlFor={`plusOneName-${guest.id}`} className="text-base font-sans tracking-wide text-[#5a3b2b] block">
                      {t('rsvp.form.plus_one_name_label')}
                    </label>
                    <input
                      id={`plusOneName-${guest.id}`}
                      required
                      name={`plusOneName-${guest.id}`}
                      placeholder={t('rsvp.form.plus_one_name_placeholder')}
                      className="w-full bg-transparent border-b-2 border-[#e2d6c8] focus:ring-2 focus:ring-[#bf9c56]/25 focus:border-[#bf9c56] focus:outline-none px-2 py-3 font-sans text-base text-[#5a3b2b] transition-all rounded-t-sm"
                      value={guest.plusOneName}
                      onChange={(e) => updateGuest(guest.id, 'plusOneName', e.target.value)}
                    />
                  </div>
                  <div className="flex flex-col space-y-2">
                    <label htmlFor={`plusOneMealChoice-${guest.id}`} className="text-base font-sans tracking-wide text-[#5a3b2b] block">
                      {t('rsvp.form.plus_one_dietary_label', { defaultValue: `Plus One ${t('rsvp.form.dietary_label')}` })}
                    </label>
                    <input
                      id={`plusOneMealChoice-${guest.id}`}
                      name={`plusOneMealChoice-${guest.id}`}
                      placeholder={t('rsvp.form.dietary_placeholder')}
                      className="w-full bg-transparent border-b-2 border-[#e2d6c8] focus:ring-2 focus:ring-[#bf9c56]/25 focus:border-[#bf9c56] focus:outline-none px-2 py-3 font-sans text-base text-[#5a3b2b] transition-all rounded-t-sm"
                      value={guest.plusOneMealChoice}
                      onChange={(e) => updateGuest(guest.id, 'plusOneMealChoice', e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="space-y-8">
            <div className="space-y-4">
              <label className="text-base font-sans tracking-wide text-[#5a3b2b] block">
                {t('rsvp.form.dietary_label')}
              </label>
              <div className="flex flex-wrap gap-4 sm:gap-8">
                {[
                  { label: t('rsvp.form.yes'), value: 'yes' },
                  { label: t('rsvp.form.no'), value: 'no' }
                ].map((option) => {
                  const isChecked = option.value === 'yes' ? guest.mealChoice !== '' : guest.mealChoice === '';
                  return (
                    <label key={option.value} className="flex items-center gap-3 cursor-pointer group min-h-[44px]">
                      <input
                        type="radio"
                        name={`hasDietary-${guest.id}`}
                        value={option.value}
                        checked={isChecked}
                        className="w-5 h-5 accent-[#bf9c56] text-[#bf9c56] bg-transparent border-[#bf9c56] focus:ring-2 focus:ring-[#bf9c56]/25 focus:ring-offset-2 focus:outline-none cursor-pointer"
                        onChange={(e) => {
                          if (e.target.value === 'no') {
                            updateGuest(guest.id, 'mealChoice', '');
                          } else if (guest.mealChoice === '') {
                            // Focus on the input instead of setting a space
                            const input = document.getElementsByName(`mealChoice-${guest.id}`)[0];
                            if (input) (input as HTMLInputElement).focus();
                          }
                        }}
                      />
                      <span className="text-base font-sans text-[#5a3b2b] cursor-pointer">
                        {option.label}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className={`flex flex-col space-y-2 transition-opacity duration-300 ${guest.mealChoice !== '' ? 'opacity-100' : 'opacity-75'}`}>
              <label htmlFor={`mealChoice-${guest.id}`} className="text-base font-sans tracking-wide text-[#5a3b2b] block">
                {t('rsvp.form.dietary_optional')}
              </label>
              <input
                id={`mealChoice-${guest.id}`}
                name={`mealChoice-${guest.id}`}
                placeholder={t('rsvp.form.dietary_placeholder')}
                className="w-full bg-transparent border-b-2 border-[#e2d6c8] focus:ring-2 focus:ring-[#bf9c56]/25 focus:border-[#bf9c56] focus:outline-none px-2 py-3 font-sans text-base text-[#5a3b2b] transition-all rounded-t-sm"
                value={guest.mealChoice}
                onChange={(e) => updateGuest(guest.id, 'mealChoice', e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex flex-col space-y-2">
              <label htmlFor={`message-${guest.id}`} className="text-base font-sans tracking-wide text-[#5a3b2b] block">
                {t('rsvp.form.message_label')}
              </label>
              <input
                id={`message-${guest.id}`}
                name={`message-${guest.id}`}
                placeholder={t('rsvp.form.message_placeholder')}
                className="w-full bg-transparent border-b-2 border-[#e2d6c8] focus:ring-2 focus:ring-[#bf9c56]/25 focus:border-[#bf9c56] focus:outline-none px-2 py-3 font-sans text-base text-[#5a3b2b] transition-all rounded-t-sm"
                value={guest.message}
                onChange={(e) => updateGuest(guest.id, 'message', e.target.value)}
              />
            </div>
          </div>
        </div>
      ))}

      {plusChildrenAllowed && (
        <div className="space-y-8 animate-fade-in mb-12">
          <div className="flex flex-col space-y-2">
            <label htmlFor="children" className="text-base font-sans tracking-wide text-[#5a3b2b] block">
              {t('rsvp.form.children_label')}
            </label>
            <input
              id="children"
              type="number"
              min="0"
              max="10"
              required
              name="children"
              className="w-full max-w-xs bg-transparent border-b-2 border-[#e2d6c8] focus:ring-2 focus:ring-[#bf9c56]/25 focus:border-[#bf9c56] focus:outline-none px-2 py-3 font-sans text-base text-[#5a3b2b] transition-all rounded-t-sm"
              value={childrenCount}
              onChange={(e) => setChildrenCount(e.target.value)}
            />
          </div>

          {parseInt(childrenCount, 10) > 0 && (
            <div className="space-y-8 animate-slide-up bg-[#fdfaf6]/40 p-6 rounded-lg border border-[#e2d6c8]/50 mt-4">
              {Array.from({ length: parseInt(childrenCount, 10) || 0 }).map((_, idx) => (
                <div key={idx} className="space-y-4 pt-4 border-t border-[#e2d6c8]/30 first:pt-0 first:border-0">
                  <div className="flex flex-col space-y-2">
                    <label htmlFor={`childName-${idx}`} className="text-base font-sans tracking-wide text-[#5a3b2b] block">
                      {t('rsvp.form.child_name_label', { index: idx + 1, defaultValue: `Child ${idx + 1} Name` })}
                    </label>
                    <input
                      id={`childName-${idx}`}
                      required
                      name={`childName-${idx}`}
                      placeholder={t('rsvp.form.child_name_placeholder', { defaultValue: `Full Name of Child ${idx + 1}` })}
                      className="w-full bg-transparent border-b-2 border-[#e2d6c8] focus:ring-2 focus:ring-[#bf9c56]/25 focus:border-[#bf9c56] focus:outline-none px-2 py-3 font-sans text-base text-[#5a3b2b] transition-all rounded-t-sm"
                      value={childrenNames[idx] || ''}
                      onChange={(e) => {
                        const updated = [...childrenNames];
                        updated[idx] = e.target.value;
                        setChildrenNames(updated);
                      }}
                    />
                  </div>
                  <div className="flex flex-col space-y-2">
                    <label htmlFor={`childDietary-${idx}`} className="text-base font-sans tracking-wide text-[#5a3b2b] block">
                      {t('rsvp.form.child_dietary_label', { index: idx + 1, defaultValue: `Child ${idx + 1} Dietary requirements (optional)` })}
                    </label>
                    <input
                      id={`childDietary-${idx}`}
                      name={`childDietary-${idx}`}
                      placeholder={t('rsvp.form.dietary_placeholder')}
                      className="w-full bg-transparent border-b-2 border-[#e2d6c8] focus:ring-2 focus:ring-[#bf9c56]/25 focus:border-[#bf9c56] focus:outline-none px-2 py-3 font-sans text-base text-[#5a3b2b] transition-all rounded-t-sm"
                      value={childrenDietary[idx] || ''}
                      onChange={(e) => {
                        const updated = [...childrenDietary];
                        updated[idx] = e.target.value;
                        setChildrenDietary(updated);
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Accommodation - per party, only when attending ES */}
      {guests.some(g => g.isPrimary && g.events.includes('ES') && g.attending.es === 'yes') && (
        <div className="space-y-4 animate-fade-in mb-12">
          <label htmlFor="accommodation" className="text-base font-sans tracking-wide text-[#5a3b2b] block">
            {t('rsvp.form.accommodation_label')}
          </label>
          <div className="relative" ref={accommodationRef}>
            <input
              id="accommodation"
              type="text"
              name="accommodation"
              autoComplete="off"
              className="w-full bg-transparent border-b-2 border-[#e2d6c8] focus:ring-2 focus:ring-[#bf9c56]/25 focus:border-[#bf9c56] focus:outline-none px-2 py-3 font-sans text-base text-[#5a3b2b] transition-all rounded-t-sm"
              placeholder={t('rsvp.form.accommodation_placeholder')}
              value={accommodation}
              onChange={(e) => {
                setAccommodation(e.target.value);
                setShowAccommodationSuggestions(true);
              }}
              onFocus={() => setShowAccommodationSuggestions(true)}
            />
            {showAccommodationSuggestions && (
              <ul className="absolute z-30 left-0 right-0 mt-1 bg-white border border-[#e2d6c8] rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {SUGGESTED_ACCOMMODATIONS
                  .filter(h => !accommodation || h.toLowerCase().includes(accommodation.toLowerCase()))
                  .map((hotel) => (
                    <li
                      key={hotel}
                      className="px-4 py-3 cursor-pointer text-[#5a3b2b] font-sans text-sm hover:bg-[#fdfaf6] transition-colors border-b border-[#e2d6c8]/30 last:border-0"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setAccommodation(hotel);
                        setShowAccommodationSuggestions(false);
                      }}
                    >
                      {hotel}
                    </li>
                  ))}
                {accommodation && !SUGGESTED_ACCOMMODATIONS.some(h => h.toLowerCase() === accommodation.toLowerCase()) && (
                  <li
                    className="px-4 py-3 cursor-pointer text-[#5a3b2b]/60 font-sans text-sm italic hover:bg-[#fdfaf6] transition-colors"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setShowAccommodationSuggestions(false);
                    }}
                  >
                    {`"${accommodation}"`}
                  </li>
                )}
              </ul>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4 mt-6">
            <div className="flex flex-col space-y-2">
              <label htmlFor="accommodationCheckIn" className="text-base font-sans tracking-wide text-[#5a3b2b] block">
                {t('rsvp.form.checkin_label')}
              </label>
              <input
                id="accommodationCheckIn"
                type="date"
                name="accommodationCheckIn"
                className="w-full bg-transparent border-b-2 border-[#e2d6c8] focus:ring-2 focus:ring-[#bf9c56]/25 focus:border-[#bf9c56] focus:outline-none px-2 py-3 font-sans text-base text-[#5a3b2b] transition-all rounded-t-sm"
                value={accommodationCheckIn}
                onChange={(e) => setAccommodationCheckIn(e.target.value)}
              />
            </div>
            <div className="flex flex-col space-y-2">
              <label htmlFor="accommodationCheckOut" className="text-base font-sans tracking-wide text-[#5a3b2b] block">
                {t('rsvp.form.checkout_label')}
              </label>
              <input
                id="accommodationCheckOut"
                type="date"
                name="accommodationCheckOut"
                className="w-full bg-transparent border-b-2 border-[#e2d6c8] focus:ring-2 focus:ring-[#bf9c56]/25 focus:border-[#bf9c56] focus:outline-none px-2 py-3 font-sans text-base text-[#5a3b2b] transition-all rounded-t-sm"
                value={accommodationCheckOut}
                onChange={(e) => setAccommodationCheckOut(e.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      <div className="pt-16 pb-8 flex flex-col items-center justify-center space-y-6">
        <button
          type="submit"
          disabled={submitting}
          className="group flex flex-row items-center justify-center space-x-3 text-xl md:text-2xl font-display text-[#bf9c56] tracking-[0.2em] md:tracking-[0.3em] uppercase transition-all hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed min-h-[56px] px-8 py-3 rounded-lg focus:outline-none focus:ring-4 focus:ring-[#bf9c56]/25 focus:border-[#bf9c56]"
        >
          {submitting ? (
            <>
              <svg className="animate-spin h-6 w-6 text-[#bf9c56]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>{t('rsvp.form.submitting')}</span>
            </>
          ) : (
            <span>{t('rsvp.form.submit_btn')}</span>
          )}
        </button>
        {status && (
          <p className="text-center text-sm font-sans italic text-red-700 bg-red-50 px-4 py-2 rounded-md animate-fade-in" role="alert" aria-live="assertive">
            {status}
          </p>
        )}
      </div>
    </form>
  )
}
